import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'
import { indexSummarySchema, marketBreadthSchema } from '@/schemas/market'

/**
 * The Market · Overview tab: one payload for the whole page.
 *
 * Ten blocks arrive together rather than as ten queries because they are
 * twelve readings of one session — the headline, the breadth bar and the
 * movers all describe the same close, and fetching them separately lets the
 * page render a 2,622.48 headline above a movers list from the next tick.
 *
 * Counts are integers; everything printed with a rupee, a decimal or a percent
 * sign is `Money`. Sparklines are plain numbers: they feed a chart and are
 * never read as a figure.
 */

export const indexTileSchema = z.object({
  id: z.string().min(1),
  /** mm-be's index key ("NEPSE", "LIFE") — what `/indices/{key}/history/` takes. */
  key: z.string().min(1),
  /** The card's eyebrow: the API name with a trailing " Sub-Index" dropped. */
  label: z.string().min(1),
  /** The API name as sent, for the accessible name and the hover title. */
  name: z.string().min(1),
  value: moneySchema,
  /** `null` when mm-be reports no rupee point change for this index. */
  pointChange: moneySchema.nullable(),
  percentChange: moneySchema,
})

export const overviewStatsSchema = z.object({
  /** "Turnover 4.82 arba" — rupees in arba, the unit the card prints. `null`
   *  when the session summary carries no turnover figure. */
  turnoverArba: moneySchema.nullable(),
  /** "Shares traded 1.42 Cr kitta". `null` when unreported. */
  sharesTradedCrKitta: moneySchema.nullable(),
  /** `null` when mm-be has no transaction count for the session. */
  transactions: z.number().int().nullable(),
  /** "Total scrips traded 244" — the second stat row of V2 `4236:86804`. */
  scripsTraded: z.number().int(),
  /** Rupees, unscaled. `null` when mm-be has no figure for the session. */
  totalMarketCap: moneySchema.nullable(),
})

/**
 * The 52-week track under "Today in numbers". `low` and `high` are nullable
 * because `/market/summary/` sends them so; the track needs both, and draws
 * nothing rather than a guessed end when either is missing. There are no
 * dates: the summary carries none for the two extremes, so none are shown.
 */
export const range52Schema = z.object({
  now: moneySchema,
  low: moneySchema.nullable(),
  high: moneySchema.nullable(),
  /** "lower third of the year"; `null` when the range cannot place `now`. */
  caption: z.string().min(1).nullable(),
})

export const moverBriefSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  ltp: moneySchema,
  percentChange: moneySchema,
  /**
   * Up to five closes, and none for a stock with no price yet — so no
   * `.length()`: one short row must not fail the mover list. `Sparkline` draws
   * nothing under two points.
   */
  sparkline: z.array(z.number()),
  /** Kitta traded today, for the movers row's preview card. `null` when mm-be sends none. */
  volume: z.number().int().nullable(),
})

export const sectorTileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  percentChange: moneySchema,
})

export const brokerHighlightSchema = z.object({
  /** The NEPSE broker number, kept as a string: "07" is not 7. */
  code: z.string().min(1),
  name: z.string().min(1),
  mostActiveIn: z.string().min(1).nullable(),
  netKitta: z.number().int(),
})

export const marketOverviewSchema = z.object({
  indexTiles: z.array(indexTileSchema),
  /**
   * The NEPSE card at the top, reusing the Home card's shape so the two
   * screens cannot describe the same index differently. `breadth` rides on it
   * and is repeated below only because the breadth bar is its own block with
   * its own loading state.
   */
  headline: indexSummarySchema,
  stats: overviewStatsSchema,
  breadth: marketBreadthSchema,
  range52: range52Schema.nullable(),
  topGainers: z.array(moverBriefSchema),
  topLosers: z.array(moverBriefSchema),
  sectorsToday: lenientList(sectorTileSchema, 'sector tile'),
  floorsheetHighlights: z.array(brokerHighlightSchema),
  mitraSays: z.string().min(1),
})

export type IndexTileData = z.infer<typeof indexTileSchema>
export type OverviewStats = z.infer<typeof overviewStatsSchema>
export type Range52 = z.infer<typeof range52Schema>
export type MoverBrief = z.infer<typeof moverBriefSchema>
export type BrokerHighlight = z.infer<typeof brokerHighlightSchema>
export type MarketOverview = z.infer<typeof marketOverviewSchema>
